// components/SpotlightTour.jsx
//
// A guided "spotlight" product tour for the KB Navigator. Dims the whole screen
// and cuts out / highlights one real on-screen element at a time, with the bot
// mascot + a callout explaining it and Back / Next / End controls. Launched by
// the NavBot ✨ button (always available in the panel header + empty state).
//
// Targets are tagged with `data-tour="…"` on the real components. Several render
// only after an async fetch (or behind a phase gate), so we poll for the target
// and fall back to a secondary selector, then to a centered callout if it never
// appears. The page is dimmed + click-locked while the tour runs.
//
// JSX/plain-CSS port of swa-botfieldPM's SpotlightTour.tsx — same mechanics
// (portal, box-shadow "hole", target polling, route navigation), minus the
// permission filter / openCoach / column / extraTarget variants we don't need.
// While mounted it flips tourDemo.setDemo(true) so the Client Groups page swaps
// in curated demo fixtures (and write paths no-op); setDemo(false) on unmount.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { tourDemo } from '../lib/tourDemo';
import './SpotlightTour.css';

const MASCOT = '/assets/kb-navigator/frame-1.png';
const PAD = 6;                 // halo padding around the highlighted element
const CALLOUT_W = 320;
const FIND_TIMEOUT_MS = 5000;  // how long to wait for an async/gated target
const POLL_MS = 120;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function SpotlightTour({ steps = [], onClose }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [centered, setCentered] = useState(false);
  const targetElRef = useRef(null);

  const step = steps[index];
  const last = index >= steps.length - 1;

  const finish = useCallback(() => { if (onClose) onClose(); }, [onClose]);

  // Curated demo data is on for the whole tour; restore real data on unmount.
  // Set before the locate effect below so the page is in demo mode the moment
  // it mounts (no flash of the real data under the overlay).
  useEffect(() => {
    tourDemo.setDemo(true);
    return () => { tourDemo.setDemo(false); };
  }, []);

  // Nothing to show — bail immediately.
  useEffect(() => {
    if (steps.length === 0) finish();
  }, [steps.length, finish]);

  // Find (and keep measuring) the current step's target. Navigates first for
  // page steps, then polls for the element, then falls back to centered.
  useEffect(() => {
    if (!step) return undefined;
    // Welcome step: no element to spotlight. Navigate (to preload the section
    // behind the dim) and render the centered welcome card.
    if (step.welcome) {
      if (step.route) navigate(step.route);
      setRect(null);
      setCentered(false);
      targetElRef.current = null;
      return undefined;
    }
    let cancelled = false;
    let poll = null;
    let backstop = null;
    const deadline = Date.now() + FIND_TIMEOUT_MS;

    setRect(null);
    setCentered(false);
    targetElRef.current = null;

    if (step.route) navigate(step.route);

    const measure = () => {
      const el = targetElRef.current;
      if (!el) return;
      setRect(el.getBoundingClientRect());
    };

    const onFound = (el) => {
      targetElRef.current = el;
      measure();
      el.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      // Re-measure after the smooth scroll settles (scroll listener also runs).
      backstop = setTimeout(measure, 420);
    };

    const tryFind = () => {
      const el =
        (step.target ? document.querySelector(step.target) : null) ||
        (step.fallback ? document.querySelector(step.fallback) : null);
      if (el) {
        if (poll) { clearInterval(poll); poll = null; }
        if (!cancelled) onFound(el);
        return;
      }
      if (Date.now() > deadline) {
        if (poll) { clearInterval(poll); poll = null; }
        if (!cancelled) setCentered(true);   // graceful fallback — explain it centered
      }
    };

    tryFind();
    // If the target wasn't present synchronously, poll until it appears or the
    // deadline trips (tryFind switches to the centered fallback on timeout).
    if (!targetElRef.current) poll = setInterval(tryFind, POLL_MS);

    const onViewport = () => measure();
    window.addEventListener('scroll', onViewport, true);
    window.addEventListener('resize', onViewport);

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (backstop) clearTimeout(backstop);
      window.removeEventListener('scroll', onViewport, true);
      window.removeEventListener('resize', onViewport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step?.target, step?.route]);

  // Esc ends the tour.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') finish(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  if (!step) return null;

  const goNext = () => { if (last) finish(); else setIndex((i) => i + 1); };
  const goBack = () => setIndex((i) => Math.max(0, i - 1));

  // Full welcome page (step 0): definitions + "Not now" / pulsing "Start" — no
  // spotlight, just a centered card over the dim.
  if (step.welcome) {
    return createPortal(
      <>
        <div className="sptour-catcher sptour-catcher--dim" onMouseDown={(e) => e.preventDefault()} />
        <div className="sptour-welcome" role="dialog" aria-label="Tour welcome">
          <div className="sptour-welcome-head">
            <img className="sptour-mascot sptour-welcome-mascot" src={MASCOT} alt="" />
            <h2 className="sptour-welcome-title">{step.title}</h2>
          </div>
          {step.body && <p className="sptour-welcome-intro">{step.body}</p>}
          {Array.isArray(step.definitions) && step.definitions.length > 0 && (
            <dl className="sptour-welcome-defs">
              {step.definitions.map((d) => (
                <div key={d.term} className="sptour-welcome-def">
                  <dt className="sptour-welcome-term">{d.term}</dt>
                  <dd className="sptour-welcome-desc">{d.def}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className="sptour-welcome-actions">
            <button className="sptour-welcome-skip" onClick={finish}>Not now</button>
            <button className="sptour-welcome-start sptour-pulse" onClick={goNext}>Start the tour →</button>
          </div>
        </div>
      </>,
      document.body,
    );
  }

  // The welcome page (step 0, when present) isn't counted in "Step N of M".
  const hasWelcome = !!(steps[0] && steps[0].welcome);
  const total = hasWelcome ? steps.length - 1 : steps.length;
  const stepNum = hasWelcome ? index : index + 1;

  // Position the callout near the highlighted rect (below if room, else above);
  // centered when there's no target.
  const showHole = !!rect && !centered;
  let calloutStyle;
  if (showHole && rect) {
    const belowTop = rect.bottom + 14;
    const fitsBelow = belowTop + 180 < window.innerHeight;
    const top = fitsBelow ? belowTop : Math.max(12, rect.top - 14 - 180);
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - CALLOUT_W / 2),
      window.innerWidth - CALLOUT_W - 12,
    );
    calloutStyle = { left, top };
  } else {
    calloutStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  }

  return createPortal(
    <>
      <div
        className={`sptour-catcher ${showHole ? '' : 'sptour-catcher--dim'}`}
        onMouseDown={(e) => e.preventDefault()}
      />
      {showHole && rect && (
        <div
          className="sptour-hole"
          style={{
            left: rect.left - PAD,
            top: rect.top - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}
      <div className="sptour-callout" style={calloutStyle} role="dialog" aria-label="Feature tour">
        <div className="sptour-head">
          <img className="sptour-mascot" src={MASCOT} alt="" />
          <div className="sptour-title">{step.title}</div>
        </div>
        <p className="sptour-body">{step.body}</p>
        <div className="sptour-controls">
          <span className="sptour-counter">Step {stepNum} of {total}</span>
          <button className="sptour-back" onClick={goBack} disabled={index === 0}>← Back</button>
          <button className="sptour-end" onClick={finish}>End</button>
          <button className="sptour-next" onClick={goNext}>{last ? 'Finish ✓' : 'Next →'}</button>
        </div>
      </div>
    </>,
    document.body,
  );
}
